/**
 * Seeded pseudo-random number generation for everything deterministic (sim, generator).
 *
 * Algorithm: sfc32 (Small Fast Counting, 128-bit state) seeded from a string with cyrb128.
 * Copied from lawnmower. A run's seed rebuilds the same rooms only while this exact sequence holds,
 * so the golden-sequence test must be updated deliberately.
 */

export type RngState = readonly [number, number, number, number];

const UINT32_RANGE = 0x1_0000_0000;

/** 128-bit string hash (cyrb128 by bryc, public domain). Returns four uint32 words. */
export function hashString128(input: string): RngState {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < input.length; i++) {
    const k = input.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

/** 32-bit string hash, handy for deriving numeric ids from seeds. */
export function hashString32(input: string): number {
  return hashString128(input)[0];
}

export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  private constructor(state: RngState) {
    [this.a, this.b, this.c, this.d] = state;
  }

  /** Creates a generator from a string seed (e.g. `"run:42/planet:moon/room:2"`). */
  static fromSeed(seed: string): Rng {
    const rng = new Rng(hashString128(seed));
    // Discard early outputs so similar seeds diverge fully.
    for (let i = 0; i < 15; i++) rng.nextUint32();
    return rng;
  }

  /** Restores a generator from a snapshot taken with {@link Rng.state}. */
  static fromState(state: RngState): Rng {
    return new Rng(state);
  }

  /** Snapshot of the internal state; restoring it reproduces the same future sequence. */
  state(): RngState {
    return [this.a >>> 0, this.b >>> 0, this.c >>> 0, this.d >>> 0];
  }

  /** Uniform integer in [0, 2^32). */
  nextUint32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }

  /** Uniform float in [0, 1). For generation only; never store floats in sim state. */
  nextFloat(): number {
    return this.nextUint32() / UINT32_RANGE;
  }

  /** Unbiased uniform integer in [0, maxExclusive). */
  nextInt(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > UINT32_RANGE) {
      throw new RangeError(`nextInt: invalid bound ${maxExclusive}`);
    }
    // Rejection sampling avoids modulo bias.
    const limit = UINT32_RANGE - (UINT32_RANGE % maxExclusive);
    let r = this.nextUint32();
    while (r >= limit) r = this.nextUint32();
    return r % maxExclusive;
  }

  /** Uniform integer in [min, max] (inclusive). */
  intBetween(min: number, max: number): number {
    if (max < min) throw new RangeError(`intBetween: max ${max} < min ${min}`);
    return min + this.nextInt(max - min + 1);
  }

  /** True with probability `numerator / denominator` (integer odds keep callers float-free). */
  chance(numerator: number, denominator: number): boolean {
    return this.nextInt(denominator) < numerator;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('pick: empty array');
    return items[this.nextInt(items.length)] as T;
  }

  /** Fisher–Yates shuffle in place; returns the same array. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.nextInt(i + 1);
      [items[i], items[j]] = [items[j] as T, items[i] as T];
    }
    return items;
  }
}
